import React, { ForwardRefRenderFunction, forwardRef } from "react";
//#region types
import { FormHandles } from "models/propTypes/genericFormProps";
import { ForwardedCostsDialogFormProps } from "models/propTypes/invoicePropTypes";
import { InvoiceFormTypes } from "models/formTypes";
import { ForwardedCostsDialogFormat } from "models/utilTypes/forwardableCostTypes";
import {
  AdvancedEditableTableFieldDefinition,
  BaseFormFieldProps,
  DisplayProps,
  NumberInputProps,
  WintFormConnect,
} from "models/propTypes/wintFormPropsTypes";
import { CurrencyEnums } from "models/enums";
import { TranslationKeys } from "translations";
//#endregion
//#region components
import {
  AdvancedEditableTable,
  CheckboxInput,
  HorizontalFormField,
  NumberInput,
  TextDisplay,
  unconnectedFormField,
  useWintForm,
  WintForm,
} from "components/common/wintForms";
import { Grid } from "@mui/material";
//#endregion
import { ForwardedCostHelper, LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

type Row = InvoiceFormTypes.ForwardedCostsDialogRow;
type CostCellProps = { isSelectable: boolean };

const t = (key: string) => LocalizationHelperInstance.getFormattedMessage(key);

// Built when the dialog opens: when this module loads, the profile's language is not applied yet.
const getFormat = (): ForwardedCostsDialogFormat => ({
  formatDate: (date?: string) =>
    date
      ? LocalizationHelperInstance.getFormattedDate(new Date(date), {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        })
      : "",
  formatAmount: (netAmountSek: number) =>
    LocalizationHelperInstance.getFormattedCurrency(netAmountSek, CurrencyEnums.CurrencyCode.SEK, 2, 2),
  awaitingBookingText: t(TranslationKeys.Invoice.ForwardedCostsAwaitingBooking),
});

const AmountCell: React.FC<DisplayProps & CostCellProps> = ({ isSelectable, ...props }) => (
  <div className={isSelectable ? undefined : cssExports.forwardedCostAwaitingBooking}>
    <TextDisplay {...props} />
  </div>
);

// A cost still waiting to be booked has no amount yet, so there is nothing to mark up.
const MarkupCell: React.FC<NumberInputProps & CostCellProps> = ({ isSelectable, ...props }) =>
  isSelectable ? <NumberInput {...props} /> : null;

const ForwardedCostsDialogForm: ForwardRefRenderFunction<FormHandles, ForwardedCostsDialogFormProps> = (props, ref) => {
  const { costs, initialSelection, onApply, onFormValidationChange } = props;
  const format = React.useMemo(getFormat, []);

  const { wintFormProps, connect, values, setValues } = useWintForm<InvoiceFormTypes.ForwardedCostsDialog>({
    formRef: ref,
    defaultValues: ForwardedCostHelper.toForwardedCostsDialogValues(costs, initialSelection, format),
    handleSubmit: (submitted: InvoiceFormTypes.ForwardedCostsDialog) =>
      onApply(ForwardedCostHelper.fromForwardedCostsDialogValues(submitted)),
    onFormValidationChange,
    enableReinitialize: false,
  });

  const selectAll = ForwardedCostHelper.getForwardedCostsSelectAllState(values.rows);
  const markupForAll = ForwardedCostHelper.getForwardedCostsMarkupForAll(values.rows);
  const markupLabel = t(TranslationKeys.Invoice.ForwardedCostsMarkup);
  const markupForAllLabel = t(TranslationKeys.Invoice.ForwardedCostsMarkupForAll);

  const fields: AdvancedEditableTableFieldDefinition<Row> = {
    selected: {
      Component: CheckboxInput,
      className: cssExports.select,
      header: (
        <CheckboxInput
          {...unconnectedFormField}
          name="selectAllForwardedCosts"
          value={selectAll.checked}
          disabled={selectAll.disabled}
          setValue={(selected: boolean) =>
            setValues((prev) => ForwardedCostHelper.selectAllForwardedCosts(prev, selected))
          }
        />
      ),
      extraProps: (row: Row) => ({ disabled: !row.isSelectable }),
    },
    sourceDocument: {
      Component: TextDisplay,
      header: t(TranslationKeys.Invoice.ForwardedCostsSource),
      className: cssExports.whiteSpaceNoWrap,
    },
    date: {
      Component: TextDisplay,
      header: t(TranslationKeys.Common.Date),
      className: cssExports.whiteSpaceNoWrap,
    },
    text: { Component: TextDisplay, header: t(TranslationKeys.Common.Description) },
    amount: {
      Component: AmountCell,
      header: t(TranslationKeys.Common.AmountExVat),
      alignment: "right",
      className: cssExports.whiteSpaceNoWrap,
      extraProps: (row: Row) => ({ isSelectable: row.isSelectable }),
    },
    markupPercentage: {
      Component: MarkupCell,
      header: markupLabel,
      alignment: "right",
      className: cssExports.forwardedCostMarkupColumn,
      extraProps: (row: Row) => ({
        isSelectable: row.isSelectable,
        decimalPoints: 2,
        allowNegative: false,
        rightAlign: true,
        inputProps: { "aria-label": markupLabel },
      }),
      footer: (
        <NumberInput
          {...unconnectedFormField}
          name="markupForAll"
          value={markupForAll.markupPercentage}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const { value } = e.target;
            if (ForwardedCostHelper.shouldApplyMarkupForAll(values.rows, value)) {
              setValues((prev) => ForwardedCostHelper.setForwardedCostsMarkupForAll(prev, value));
            }
          }}
          placeholder={markupForAll.isMixed ? t(TranslationKeys.Invoice.ForwardedCostsMarkupMixed) : undefined}
          disabled={selectAll.disabled}
          decimalPoints={2}
          allowNegative={false}
          rightAlign
          inputProps={{ "aria-label": markupForAllLabel }}
        />
      ),
    },
  };

  return (
    <WintForm {...wintFormProps}>
      <Grid item xs={12}>
        <AdvancedEditableTable<Row>
          className={cssExports.forwardedCostsTable}
          connect={connect as WintFormConnect<any>}
          name="rows"
          value={values.rows}
          fields={fields}
          idField="costId"
          defaultRow={{}}
          isCompactTable
          extraCompact
          showColumnHeaders
          footerLabel={markupForAllLabel}
          hideActions
          hideActionsCell
        />
      </Grid>
      <Grid item container direction="column">
        <HorizontalFormField<BaseFormFieldProps>
          Component={CheckboxInput}
          {...connect("attachDocuments")}
          label={t(TranslationKeys.Invoice.ForwardedCostsAttachDocuments)}
          mainLabelGridSize={true}
          childGridSize={"auto"}
          verticalAlignCenter
        />
      </Grid>
    </WintForm>
  );
};

export default forwardRef(ForwardedCostsDialogForm);
